import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiError } from "../../lib/api";
import { useCrmMeta } from "../../hooks/useCrmMeta";
import { useAuth } from "../../context/AuthContext";
import { Plus, Target, Trophy, Percent, Trash2, HelpCircle, X } from "lucide-react";

const money = (v) =>
    `₱${Number(v || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const s = (v, fallback = "—") => {
    if (v == null || v === "") return fallback;
    if (typeof v === "string" || typeof v === "number") return v;
    return fallback;
};

const emptyForm = {
    deal_name: "",
    account_id: "",
    contact_id: "",
    contact_name: "",
    stage: "",
    amount: "",
    closing_date: "",
    source: "",
    description: "",
};

export default function Pipeline() {
    const { meta } = useCrmMeta();
    const { user } = useAuth();
    const isAdmin = user?.role === "admin" || user?.role === "owner";
    const nav = useNavigate();

    const [board, setBoard] = useState([]);
    const [totals, setTotals] = useState({});
    const [loading, setLoading] = useState(true);
    const [errored, setErrored] = useState(false);
    const [busyDealId, setBusyDealId] = useState(null);

    const [showCreate, setShowCreate] = useState(false);
    const [showGuide, setShowGuide] = useState(false);
    const [form, setForm] = useState(emptyForm);
    const [saving, setSaving] = useState(false);
    const [accounts, setAccounts] = useState([]);
    const [contacts, setContacts] = useState([]);

    const load = async () => {
        setLoading(true);
        setErrored(false);
        try {
            const { data } = await api.get("/crm/pipeline");
            setBoard(Array.isArray(data?.board) ? data.board : []);
            setTotals(data && typeof data.totals === "object" && data.totals ? data.totals : {});
        } catch (e) {
            setErrored(true);
            toast.error(formatApiError(e));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); }, []);

    const openCreate = async () => {
        setShowCreate(true);
        try {
            const { data } = await api.get("/crm/accounts");
            setAccounts(Array.isArray(data) ? data : []);
        } catch (e) {
            setAccounts([]);
        }
    };

    const updateForm = (key, value) => setForm((f) => ({ ...f, [key]: value }));

    const onAccountChange = async (accountId) => {
        updateForm("account_id", accountId);
        updateForm("contact_id", "");
        if (!accountId) { setContacts([]); return; }
        try {
            const { data } = await api.get("/crm/contacts", { params: { accountId } });
            setContacts(Array.isArray(data) ? data : []);
        } catch (e) {
            setContacts([]);
        }
    };

    const resetForm = () => {
        setForm(emptyForm);
        setShowCreate(false);
        setContacts([]);
    };

    const createDeal = async () => {
        if (!form.deal_name.trim()) {
            toast.error("Deal name is required.");
            return;
        }
        setSaving(true);
        try {
            await api.post("/crm/deals", {
                ...form,
                account_id: form.account_id || null,
                contact_id: form.contact_id || null,
                amount: form.amount ? Number(form.amount) : 0,
                stage: form.stage || (meta.open_stages || [])[0] || "",
            });
            toast.success("Deal created");
            resetForm();
            await load();
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setSaving(false);
        }
    };

    const changeStage = async (dealId, newStage) => {
        setBusyDealId(dealId);
        try {
            await api.put(`/crm/deals/${dealId}`, { stage: newStage });
            toast.success("Stage updated");
            await load();
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setBusyDealId(null);
        }
    };

    // Admin-only permanent delete of an opportunity.
    const deleteDeal = async (d) => {
        if (!window.confirm(`Delete opportunity "${s(d.deal_name)}"? This permanently removes it.`)) return;
        setBusyDealId(d.id);
        try {
            await api.delete(`/crm/deals/${d.id}`);
            toast.success("Opportunity deleted");
            await load();
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setBusyDealId(null);
        }
    };

    // Edit the deal value inline; backend recomputes expected_revenue from stage probability.
    const changeAmount = async (dealId, amount) => {
        setBusyDealId(dealId);
        try {
            await api.put(`/crm/deals/${dealId}`, { amount });
            toast.success("Value updated");
            await load();
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setBusyDealId(null);
        }
    };

    const allStages = [
        ...(meta.deal_stages || []).map((d) => d.stage),
    ];

    if (loading) {
        return (
            <div className="p-3 md:p-6" data-testid="crm-pipeline-page">
                <div className="sc-card p-8 text-center text-sm text-muted-foreground">Loading pipeline…</div>
            </div>
        );
    }

    if (errored) {
        return (
            <div className="p-3 md:p-6" data-testid="crm-pipeline-page">
                <div className="sc-card p-8 text-center text-sm text-muted-foreground">Couldn't load the pipeline.</div>
            </div>
        );
    }

    return (
        <div className="p-3 md:p-6 space-y-5" data-testid="crm-pipeline-page">
            <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-3">
                <div>
                    <div className="sc-section-title">CRM</div>
                    <h1 className="text-xl md:text-2xl font-semibold tracking-tight">Pipeline</h1>
                </div>

                <div className="flex items-center gap-2">
                    <button onClick={() => setShowGuide(true)} className="h-9 inline-flex items-center gap-1 px-3 rounded border border-border text-sm hover:bg-secondary" data-testid="pipeline-guide-button">
                        <HelpCircle className="w-4 h-4" /> How to use
                    </button>
                    <button onClick={openCreate} className="h-9 inline-flex items-center gap-1 px-4 rounded bg-foreground text-background text-sm" data-testid="deal-add-button">
                        <Plus className="w-4 h-4" /> New Deal
                    </button>
                </div>
            </div>

            {showGuide && <PipelineGuide isAdmin={isAdmin} onClose={() => setShowGuide(false)} />}

            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                <SummaryCard icon={Target} label="Open Amount" value={totals.open_amount != null ? money(totals.open_amount) : "—"} />
                <SummaryCard icon={Trophy} label="Weighted Forecast" value={totals.weighted_forecast != null ? money(totals.weighted_forecast) : "—"} />
                <SummaryCard label="Open / Won / Lost" value={`${totals.open_count ?? 0} / ${totals.won_count ?? 0} / ${totals.lost_count ?? 0}`} />
                <SummaryCard icon={Percent} label="Win Rate" value={totals.win_rate != null ? `${Number(totals.win_rate).toFixed(1)}%` : "—"} />
            </div>

            <div className="overflow-x-auto pb-2">
                <div className="flex gap-3 min-w-max">
                    {board.length === 0 && (
                        <div className="sc-card p-8 text-center text-sm text-muted-foreground w-full">No pipeline stages configured.</div>
                    )}

                    {board.map((col) => (
                        <div key={col.stage} className="w-72 shrink-0 sc-card overflow-hidden flex flex-col" data-testid={`pipeline-col-${col.stage}`}>
                            <div className="px-3 py-2 border-b border-border bg-secondary/60">
                                <div className="flex items-center justify-between">
                                    <div className="text-sm font-medium">{s(col.stage)}</div>
                                    <span className="sc-pill bg-secondary border border-border">{col.count ?? (col.deals || []).length}</span>
                                </div>
                                <div className="text-xs text-muted-foreground mt-0.5">{money(col.amount)}</div>
                                {col.probability != null && (
                                    <div className="text-[11px] text-muted-foreground">{col.probability}% probability</div>
                                )}
                            </div>

                            <div className="p-2 space-y-2 flex-1 max-h-[65vh] overflow-y-auto">
                                {(col.deals || []).length === 0 && (
                                    <div className="text-xs text-muted-foreground text-center py-6">No deals</div>
                                )}

                                {(col.deals || []).map((d) => (
                                    <div
                                        key={d.id}
                                        className="rounded-md border border-border p-2.5 hover:bg-secondary/40 cursor-pointer"
                                        onClick={() => nav(`/crm/deals/${d.id}`)}
                                        data-testid={`pipeline-deal-${d.id}`}
                                    >
                                        <div className="flex items-start justify-between gap-1">
                                            <div className="text-sm font-medium truncate">{s(d.deal_name)}</div>
                                            {isAdmin && (
                                                <button
                                                    onClick={(e) => { e.stopPropagation(); deleteDeal(d); }}
                                                    disabled={busyDealId === d.id}
                                                    className="shrink-0 text-muted-foreground hover:text-rose-600 disabled:opacity-40"
                                                    title="Delete opportunity (admin)"
                                                    data-testid={`pipeline-deal-delete-${d.id}`}
                                                >
                                                    <Trash2 className="w-3.5 h-3.5" />
                                                </button>
                                            )}
                                        </div>
                                        <div className="text-xs text-muted-foreground truncate">{s(d.account_name)}</div>

                                        <div className="mt-1 flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
                                            <span className="text-xs text-muted-foreground">₱</span>
                                            <input
                                                type="number"
                                                step="0.01"
                                                min="0"
                                                defaultValue={Number(d.amount || 0)}
                                                disabled={busyDealId === d.id}
                                                onBlur={(e) => {
                                                    const v = Number(e.target.value);
                                                    if (Number.isFinite(v) && v !== Number(d.amount || 0)) changeAmount(d.id, v);
                                                }}
                                                onKeyDown={(e) => { if (e.key === "Enter") e.currentTarget.blur(); }}
                                                className="sc-input h-7 text-xs font-mono flex-1"
                                                title="Edit deal value"
                                                data-testid={`pipeline-deal-amount-${d.id}`}
                                            />
                                        </div>

                                        <select
                                            className="sc-input h-7 text-xs mt-2"
                                            value={d.stage || col.stage}
                                            disabled={busyDealId === d.id}
                                            onClick={(e) => e.stopPropagation()}
                                            onChange={(e) => changeStage(d.id, e.target.value)}
                                            data-testid={`pipeline-deal-stage-${d.id}`}
                                        >
                                            {allStages.map((st) => (
                                                <option key={st} value={st}>{st}</option>
                                            ))}
                                        </select>
                                    </div>
                                ))}
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            {showCreate && (
                <div className="fixed inset-0 bg-black/40 grid place-items-center p-4 z-50" onClick={resetForm}>
                    <div className="sc-card p-5 w-full max-w-2xl space-y-4 bg-background max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="deal-create-modal">
                        <div>
                            <div className="text-xs uppercase tracking-wider text-muted-foreground">Create Deal</div>
                            <div className="text-lg font-semibold">New Opportunity</div>
                        </div>

                        <div className="grid md:grid-cols-2 gap-3">
                            <Field label="Deal Name" required full>
                                <input className="sc-input" value={form.deal_name} onChange={(e) => updateForm("deal_name", e.target.value)} data-testid="deal-name-input" />
                            </Field>

                            <Field label="Account">
                                <select className="sc-input" value={form.account_id} onChange={(e) => onAccountChange(e.target.value)}>
                                    <option value="">No account</option>
                                    {accounts.map((a) => (
                                        <option key={a.id} value={a.id}>{s(a.account_name)}</option>
                                    ))}
                                </select>
                            </Field>

                            <Field label="Contact">
                                <select className="sc-input" value={form.contact_id} onChange={(e) => updateForm("contact_id", e.target.value)} disabled={!form.account_id}>
                                    <option value="">No contact</option>
                                    {contacts.map((c) => (
                                        <option key={c.id} value={c.id}>{`${s(c.first_name, "")} ${s(c.last_name, "")}`.trim()}</option>
                                    ))}
                                </select>
                            </Field>

                            <Field label="Contact Name (free text)">
                                <input className="sc-input" value={form.contact_name} onChange={(e) => updateForm("contact_name", e.target.value)} placeholder="Optional if no contact selected" />
                            </Field>

                            <Field label="Stage">
                                <select className="sc-input" value={form.stage} onChange={(e) => updateForm("stage", e.target.value)}>
                                    <option value="">Default</option>
                                    {(meta.deal_stages || []).map((st) => (
                                        <option key={st.stage} value={st.stage}>{st.stage}</option>
                                    ))}
                                </select>
                            </Field>

                            <Field label="Amount">
                                <input type="number" step="0.01" className="sc-input" value={form.amount} onChange={(e) => updateForm("amount", e.target.value)} />
                            </Field>

                            <Field label="Closing Date">
                                <input type="date" className="sc-input" value={form.closing_date} onChange={(e) => updateForm("closing_date", e.target.value)} />
                            </Field>

                            <Field label="Source">
                                <select className="sc-input" value={form.source} onChange={(e) => updateForm("source", e.target.value)}>
                                    <option value="">Select source…</option>
                                    {(meta.lead_sources || []).map((src) => (
                                        <option key={src} value={src}>{src}</option>
                                    ))}
                                </select>
                            </Field>

                            <Field label="Description" full>
                                <textarea className="sc-input h-auto py-2" rows={3} value={form.description} onChange={(e) => updateForm("description", e.target.value)} />
                            </Field>
                        </div>

                        <div className="flex justify-end gap-2">
                            <button onClick={resetForm} className="h-9 px-3 rounded border border-border text-sm">Cancel</button>
                            <button onClick={createDeal} disabled={saving} className="h-9 px-4 rounded bg-foreground text-background text-sm disabled:opacity-50" data-testid="deal-save">
                                {saving ? "Creating…" : "Create Deal"}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

// Step-by-step user manual for the Pipeline (in-app help).
function PipelineGuide({ isAdmin, onClose }) {
    const steps = [
        ["Read the top summary", "The four cards show your Open Amount (value of deals still in play), Weighted Forecast (open value adjusted by each stage's win probability), your Open / Won / Lost counts, and your Win Rate."],
        ["Understand a column", "Each column is a stage. Its header shows how many deals it holds, their total value, and that stage's probability. Deals flow left-to-right from the first stage toward Closed Won (or Closed Lost)."],
        ["Add a new deal", "Click “New Deal” (top right). Enter the Deal Name (required), optionally pick an Account and Contact, choose the starting Stage, set the Amount, Closing Date and Source, then click “Create Deal.” The deal appears in the stage you chose."],
        ["Move a deal to the next stage", "On a deal card, use the stage dropdown and pick the new stage (e.g. Qualification → Proposal → Negotiation → Closed Won). The card moves to that column and the probability and weighted forecast recompute automatically."],
        ["Update a deal's value", "Click the ₱ amount box on the card, type the new value, and press Enter (or click away). It saves immediately and the forecast updates — you don't need to open the deal."],
        ["Open a deal's full details", "Click anywhere on the card (except the amount box or the stage dropdown) to open the deal page, where you can view/edit every field and link a Quotation or Sales Order."],
        ["Win or lose a deal", "Set the stage to Closed Won (it counts toward Won and your Win Rate) or Closed Lost. Either way the deal leaves the open forecast."],
    ];
    if (isAdmin) {
        steps.push(["Delete an opportunity (admin)", "Click the trash icon at the top-right of a card to permanently remove that opportunity. An audit record is kept."]);
    }
    return (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose}>
            <div className="sc-card bg-background w-full max-w-2xl max-h-[90vh] overflow-y-auto p-5 space-y-4" onClick={(e) => e.stopPropagation()} data-testid="pipeline-guide-modal">
                <div className="flex items-start justify-between gap-3">
                    <div>
                        <div className="sc-section-title">CRM · Pipeline</div>
                        <h2 className="text-lg font-semibold">How to use the Pipeline</h2>
                        <p className="text-sm text-muted-foreground mt-1">
                            The Pipeline is your deal board. Each column is a stage and every card is a deal (opportunity); move deals along as they progress and the forecast updates itself. You see only the deals you own — your Branch Manager and Admin see the wider pipeline.
                        </p>
                    </div>
                    <button onClick={onClose} className="h-8 w-8 grid place-items-center rounded-md border border-border hover:bg-secondary shrink-0" title="Close">
                        <X className="w-4 h-4" />
                    </button>
                </div>

                <ol className="space-y-3">
                    {steps.map(([title, body], i) => (
                        <li key={i} className="flex gap-3">
                            <span className="shrink-0 h-6 w-6 grid place-items-center rounded-full bg-foreground text-background text-xs font-semibold">{i + 1}</span>
                            <div>
                                <div className="text-sm font-medium">{title}</div>
                                <div className="text-sm text-muted-foreground mt-0.5">{body}</div>
                            </div>
                        </li>
                    ))}
                </ol>

                <div className="flex justify-end pt-2 border-t border-border">
                    <button onClick={onClose} className="h-9 px-4 rounded-md bg-foreground text-background text-sm font-medium">Got it</button>
                </div>
            </div>
        </div>
    );
}

function SummaryCard({ icon: Icon, label, value }) {
    return (
        <div className="sc-card p-4">
            <div className="flex items-center justify-between gap-2">
                <div>
                    <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</div>
                    <div className="text-lg font-semibold mt-1">{value}</div>
                </div>
                {Icon && <Icon className="w-5 h-5 text-muted-foreground" />}
            </div>
        </div>
    );
}

function Field({ label, required = false, full = false, children }) {
    return (
        <label className={`space-y-1 block ${full ? "md:col-span-2" : ""}`}>
            <div className="text-xs font-medium">
                {label}
                {required && <span className="text-red-500 ml-1">*</span>}
            </div>
            {children}
        </label>
    );
}
